const DAY = 1000 * 60 * 60 * 24;

export function pad2(v) {
  return ''.padStart.apply(v, [2, 0]);
}

export function formatDate([year, month, day], format) {
  return format
    .replace(/y+/, year)
    .replace('MM', pad2(month))
    .replace('M', month)
    .replace('dd', pad2(day))
    .replace('d', day);
}

export default class DateData {
  constructor(date, format = 'yyyy-MM-dd') {
    this.isToday = false;
    this.format = format;
    this.init(date);
  }

  init(date) {
    [this.yearFormat] = this.format.match(/y+/) || [null];
    [this.monthFormat] = this.format.match(/M+/) || [null];
    [this.dayFormat] = this.format.match(/d+/) || [null];
    const [year, month, day] = this.parse(date);
    if (this.isToday) {
      this.year = 9999;
      this.month = 99;
      this.day = 99;
    } else {
      this.year = this.yearFormat ? year : 1949;
      this.month = this.monthFormat ? month : 1;
      this.day = this.dayFormat ? day : 1;
    }
    this.date = this.generateDate();
  }

  generateDate() {
    return this.isToday ? null : new Date(`${this.year}/${pad2(this.month)}/${pad2(this.day)}`);
  }

  getPart(str, fragment) {
    const position = this.format.indexOf(fragment);
    if (position >= 0) {
      return Number(str.slice(position, position + fragment.length));
    }
    return null;
  }

  parse(date = new Date()) {
    // 日期数据直接解析
    if (date instanceof Date) {
      return [date.getFullYear(), date.getMonth() + 1, date.getDate()];
    }
    // 至今
    if (/^99+$/.test(String(date).replace(/[^\d]/g, ''))) {
      this.isToday = true;
      return [9999, 99, 99];
    }

    // 纯数字,没有格式的日期, 如 20200101 此类数据如果需要转换成时间, 需要format的length与date.length相等才可以解析
    if (typeof date === 'string' && /^\d+$/.test(date) && date.length <= 8 && date.length === this.format.length) {
      return [
        this.getPart(date, this.yearFormat) || 1949,
        this.getPart(date, this.monthFormat) || 1,
        this.getPart(date, this.dayFormat) || 1,
      ];
    }

    // 时间戳, 数字类型的数据作为时间戳处理, 1970-01-01 08:00:00 的时间戳 === 0
    if (typeof date === 'number') {
      const curDate = new Date(date);
      return [curDate.getFullYear(), curDate.getMonth() + 1, curDate.getDate()];
    }

    // 非纯数字的时间, 需要date字符串与format 的格式一致才能解析
    if (typeof date === 'string' && /[^yMd]/.test(this.format) && this.format.replace(/[yMd]+/g, '') === date.replace(/\d+/g, '')) {
      const patterns = this.format.split(/[^yMd]+/);
      const arr = date.split(/[^\d]+/);
      if (patterns.length === arr.length) {
        let year = 1949;
        let month = 1;
        let day = 1;
        patterns.forEach((pattern, index) => {
          if (this.yearFormat === pattern) {
            year = Number(arr[index]);
          }
          if (this.monthFormat === pattern) {
            month = Number(arr[index]);
          }
          if (this.dayFormat === pattern) {
            day = Number(arr[index]);
          }
        });
        return [year, month, day];
      }
    }
    throw new Error(`无效的日期数据: ${date} ${this.format}`);
  }

  valueOf() {
    return this.isToday ? Number.MAX_VALUE : this.date.getTime();
  }

  getDate() {
    return this.date;
  }

  fitleValue() {
    console.error('不要在用这个接口了, 准备下线了! 使用 filterValue 替换');
    return this.filterValue();
  }

  filterValue() {
    const newArr = [];
    this.yearFormat && newArr.push(this.year);
    this.monthFormat && newArr.push(this.month);
    this.dayFormat && newArr.push(this.day);
    return newArr;
  }

  addYear(year, format) {
    if (this.isToday) {
      return this;
    }
    return this.newInstance({ year: this.year + year }, format);
  }

  addMonth(month, format) {
    if (this.isToday) {
      return this;
    }
    const ms = this.month + month - 1;
    const newDate = this.newInstance({
      year: this.year + Math.floor(ms / 12),
      month: (ms % 12) + 1,
      day: this.day,
    }, format);
    return newDate;
  }

  addDay(day, format = this.format) {
    if (this.isToday) {
      return this;
    }
    const newDate = new Date(this.date.getTime() + day * DAY);
    return new DateData(newDate, format);
  }

  setYear(year) {
    if (this.isToday) {
      return this;
    }
    if (this.yearFormat && /^\d+$/.test(year)) {
      this.year = Number(year);
      this.date = this.generateDate();
    }
    return this;
  }

  setMonth(month) {
    if (this.isToday) {
      return this;
    }
    if (this.monthFormat && /^\d+$/.test(month)) {
      this.month = Number(month);
      this.date = this.generateDate();
    }
    return this;
  }

  setDay(day) {
    if (this.isToday) {
      return this;
    }
    if (this.dayFormat && /^\d+$/.test(day)) {
      this.day = Number(day);
      this.date = this.generateDate();
    }
    return this;
  }

  showValue(format) {
    console.error('不要在用这个接口了, 准备下线了, 使用 formatDate 替换');
    return this.formatDate(format);
  }

  formatDate(format) {
    return formatDate(this.value(), format || this.format);
  }

  value() {
    return [this.year, this.month, this.day];
  }

  newInstance({ year = this.year, month = this.month, day = this.day }, format = this.format) {
    return new DateData(formatDate([year, month, day], format), format);
  }
}
